/**
 * Button Component - Theme-aware button
 * Pattern based on mcp-admin and github-admin styles
 */

const React = (globalThis as any).React;
const { useState } = React;

import { styles } from '../styles';

export interface ButtonProps {
  children: React.ReactNode;
  type?: 'primary' | 'danger' | 'success' | 'default';
  size?: 'small' | 'medium' | 'large';
  icon?: React.ReactNode;
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  style?: React.CSSProperties;
  'aria-label'?: string;
  htmlType?: 'button' | 'submit' | 'reset';
}

export function Button({
  children,
  type = 'default',
  size = 'medium',
  icon,
  loading = false,
  disabled = false,
  onClick,
  style,
  'aria-label': ariaLabel,
  htmlType = 'button',
}: ButtonProps) {
  const [isHovered, setIsHovered] = useState(false);
  
  const sizeStyles = {
    small: { padding: '6px 12px', fontSize: '12px' },
    medium: { padding: '10px 20px', fontSize: '14px' },
    large: { padding: '14px 28px', fontSize: '16px' },
  };
  
  const typeStyles = {
    primary: styles.buttonPrimary,
    danger: styles.buttonDanger,
    success: styles.buttonSuccess,
    default: {},
  };
  
  const buttonStyle: React.CSSProperties = {
    ...styles.button,
    ...typeStyles[type],
    ...sizeStyles[size],
    ...(isHovered && !disabled && !loading ? styles.buttonHover : {}),
    ...(disabled || loading ? styles.buttonDisabled : {}),
    ...style,
  };
  
  // WCAG: Keyboard navigation (Enter/Space)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled && !loading && onClick) {
      e.preventDefault();
      onClick();
    }
  };
  
  return React.createElement('button', {
    type: htmlType,
    onClick: disabled || loading ? undefined : onClick,
    disabled: disabled || loading,
    style: buttonStyle,
    onMouseEnter: () => setIsHovered(true),
    onMouseLeave: () => setIsHovered(false),
    onKeyDown: handleKeyDown,
    onFocus: (e: any) => {
      // WCAG: Visible focus indicator
      e.currentTarget.style.outline = '2px solid var(--main-accent)';
      e.currentTarget.style.outlineOffset = '2px';
    },
    onBlur: (e: any) => {
      e.currentTarget.style.outline = 'none';
    },
    'aria-label': ariaLabel,
    'aria-busy': loading,
    'aria-disabled': disabled || loading,
    tabIndex: disabled ? -1 : 0,
  },
    loading ? React.createElement('span', {
      style: {
        display: 'inline-block',
        width: '14px',
        height: '14px',
        border: '2px solid currentColor',
        borderTopColor: 'transparent',
        borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        marginRight: '8px',
      },
    }) : icon ? React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center' } }, icon) : null,
    children
  );
}

